import type {
  CommentAnalysisByDemographicRow,
  CommentAnalysisByTheme,
  CommentAnalysisCommentType,
  CommentAnalysisSummary,
  CommentSentiment,
  CommentThemePage,
  ContrastByDemographicData,
  ContrastData,
  ContrastDemographicOption,
  CreateProjectPayload,
  DemographicType,
  ExportJob,
  GlobalFilter,
  Industry,
  LanguagesTemplateJob,
  LanguagesTemplateJobStatus,
  PaginatedCommentAnalysisSentiment,
  PaginatedResponse,
  ProgressReportPdfJob,
  ProgressReportPdfJobStatus,
  Project,
  ProjectFilters,
  ProjectSummary,
  QuestionBreakdownByDemographicData,
  QuestionBreakdownData,
  QuestionBreakdownGroupBy,
  QuestionOption,
  SpsSurvey,
} from '../types'
import { useDssApiRequestComposable } from '@shared/composables/dss-api-integration.composable'
import { computed, ref } from 'vue'
import { SpsService } from '../services'
import { LanguageTemplateValidationError } from '../types/exceptions'

export const PROGRESS_REPORT_PDF_JOB_EXPIRED = 'PROGRESS_REPORT_PDF_JOB_EXPIRED'
export const PROGRESS_REPORT_PDF_JOB_TIMEOUT = 'PROGRESS_REPORT_PDF_JOB_TIMEOUT'

const PROGRESS_REPORT_PDF_POLL_INITIAL_DELAY_MS = 2000
const PROGRESS_REPORT_PDF_POLL_MAX_DELAY_MS = 5000
const PROGRESS_REPORT_PDF_POLL_TIMEOUT_MS = 5 * 60 * 1000

const PROGRESS_REPORT_PDF_TERMINAL_STATUSES = new Set<ProgressReportPdfJobStatus['status']>(
  ['completed', 'failed', 'timed_out', 'cancelled'],
)

async function pollProgressReportPdfJob(id: string, jobId: string): Promise<ProgressReportPdfJobStatus> {
  const deadline = Date.now() + PROGRESS_REPORT_PDF_POLL_TIMEOUT_MS
  let delay = PROGRESS_REPORT_PDF_POLL_INITIAL_DELAY_MS

  while (true) {
    if (Date.now() >= deadline)
      throw new Error(PROGRESS_REPORT_PDF_JOB_TIMEOUT)

    try {
      const { data } = await SpsService.getProgressReportPdfJobStatus(id, jobId)
      if (PROGRESS_REPORT_PDF_TERMINAL_STATUSES.has(data.status))
        return data
    }
    catch (error) {
      if ((error as { response?: { status?: number } })?.response?.status === 404)
        throw new Error(PROGRESS_REPORT_PDF_JOB_EXPIRED)
      throw error
    }

    await new Promise(resolve => setTimeout(resolve, delay))
    delay = Math.min(delay + 1000, PROGRESS_REPORT_PDF_POLL_MAX_DELAY_MS)
  }
}

async function cancelProgressReportPdfJob(id: string, jobId: string): Promise<void> {
  try {
    await SpsService.cancelProgressReportPdfJob(id, jobId)
  }
  catch (error) {
    console.error(error)
  }
}

export const LANGUAGES_TEMPLATE_JOB_EXPIRED = 'LANGUAGES_TEMPLATE_JOB_EXPIRED'
export const LANGUAGES_TEMPLATE_JOB_TIMEOUT = 'LANGUAGES_TEMPLATE_JOB_TIMEOUT'

const LANGUAGES_TEMPLATE_POLL_INITIAL_DELAY_MS = 2000
const LANGUAGES_TEMPLATE_POLL_MAX_DELAY_MS = 5000
const LANGUAGES_TEMPLATE_POLL_TIMEOUT_MS = 10 * 60 * 1000

const LANGUAGES_TEMPLATE_TERMINAL_STATUSES = new Set<LanguagesTemplateJobStatus['status']>(
  ['completed', 'failed', 'cancelled'],
)

async function pollLanguagesTemplateJob(id: string, jobId: string): Promise<LanguagesTemplateJobStatus> {
  const deadline = Date.now() + LANGUAGES_TEMPLATE_POLL_TIMEOUT_MS
  let delay = LANGUAGES_TEMPLATE_POLL_INITIAL_DELAY_MS

  while (true) {
    if (Date.now() >= deadline)
      throw new Error(LANGUAGES_TEMPLATE_JOB_TIMEOUT)

    try {
      const { data } = await SpsService.getLanguagesTemplateJobStatus(id, jobId)
      if (LANGUAGES_TEMPLATE_TERMINAL_STATUSES.has(data.status))
        return data
    }
    catch (error) {
      // The job state lives in a TTL cache; a 404 means it aged out rather
      // than that the request was malformed.
      if ((error as { response?: { status?: number } })?.response?.status === 404)
        throw new Error(LANGUAGES_TEMPLATE_JOB_EXPIRED)
      throw error
    }

    await new Promise(resolve => setTimeout(resolve, delay))
    delay = Math.min(delay + 1000, LANGUAGES_TEMPLATE_POLL_MAX_DELAY_MS)
  }
}

async function cancelLanguagesTemplateJob(id: string, jobId: string): Promise<void> {
  try {
    await SpsService.cancelLanguagesTemplateJob(id, jobId)
  }
  catch (error) {
    console.error(error)
  }
}

/**
 * The API keeps one question per page across the SIF and PSM blocks, so a page
 * holding several is split back into one page each, with the disclaimer
 * repeated. Shared by the survey save and by the publish that carries the
 * survey with it: both reach the same backend writer, so both send one shape.
 */
function buildSurveyPayload(payload: SpsSurvey): SpsSurvey {
  const pages = payload.pages.flatMap((page) => {
    const [disclaimerElement, ...elements] = page.elements ?? []
    const { disclaimer } = disclaimerElement ?? {}

    if (disclaimer === 'sif' || disclaimer === 'psm') {
      return elements.map(element => ({
        ...page,
        elements: [disclaimerElement, element],
      }))
    }

    return page
  })

  return { ...payload, pages }
}

export function useProjects() {
  const totalSurveys = ref<ProjectSummary[]>([])
  const surveys = ref<ProjectSummary[]>([])
  const pagination = ref<Omit<PaginatedResponse<ProjectSummary>, 'docs'>>({
    totalDocs: 0,
    page: 1,
    limit: 6,
    totalPages: 0,
  })
  const { isLoading, hasError, executeRequest } = useDssApiRequestComposable()

  const fetchProjects = async ({
    filters = {},
  }: {
    filters?: ProjectFilters
  } = {}): Promise<PaginatedResponse<ProjectSummary> | undefined> => {
    try {
      const [response] = await executeRequest([
        SpsService.getSurveys({ filters, page: pagination.value.page, pageSize: pagination.value.limit }),
      ])

      surveys.value = response.data.docs
      pagination.value = {
        totalDocs: response.data.totalDocs,
        page: response.data.page,
        limit: response.data.limit,
        totalPages: response.data.totalPages,
      }

      return response.data
    }
    catch (error) {
      surveys.value = []
      pagination.value = {
        totalDocs: 0,
        page: 1,
        limit: 6,
        totalPages: 0,
      }
      throw error
    }
  }

  const fetchProjectDetails = async (id: string): Promise<Project | undefined> => {
    try {
      const [response] = await executeRequest([
        SpsService.getSurveyDetails(id),
      ])
      if (response.status !== 200)
        throw Error

      return response.data
    }
    catch (error) {
      console.error(error)
      return undefined
    }
  }

  const createProject = async (payload: CreateProjectPayload): Promise<ProjectSummary> => {
    try {
      const [response] = await executeRequest([
        SpsService.createProject(payload),
      ])

      if (response.status !== 201)
        return undefined

      return response.data
    }
    catch (error) {
      console.error(error)
      return undefined
    }
  }

  const updateProject = async (id: string, payload: CreateProjectPayload): Promise<ProjectSummary> => {
    try {
      const [response] = await executeRequest([
        SpsService.updateProject(id, payload),
      ])

      return response.data
    }
    catch (error) {
      console.error(error)
      return undefined
    }
  }

  const fetchSurveyJSON = async (id: string): Promise<SpsSurvey | undefined> => {
    try {
      const [response] = await executeRequest([SpsService.getSurveyJSON(id)])
      return response.data as SpsSurvey
    }
    catch (error) {
      console.error(error)
      return undefined
    }
  }

  const updateSurveyJSON = async (id: string, payload: SpsSurvey): Promise<boolean> => {
    try {
      const [response] = await executeRequest([
        SpsService.updateSurveyJSON(id, buildSurveyPayload(payload)),
      ])
      return response.status >= 200 && response.status < 300
    }
    catch (error) {
      console.error(error)
      return false
    }
  }

  const downloadLanguagesTemplate = async (id: string, languages: string[]): Promise<Blob | undefined> => {
    try {
      const [response] = await executeRequest([
        SpsService.getLanguagesTemplate(id, languages),
      ])
      return response.data
    }
    catch (error) {
      console.error(error)
      return undefined
    }
  }

  const enqueueLanguagesTemplate = async (id: string, languages: string[]): Promise<LanguagesTemplateJob> => {
    const [response] = await executeRequest([
      SpsService.enqueueLanguagesTemplate(id, languages),
    ])
    return response.data
  }

  const downloadLanguagesTemplateJob = async (id: string, jobId: string): Promise<Blob | undefined> => {
    const [response] = await executeRequest([
      SpsService.downloadLanguagesTemplateJob(id, jobId),
    ])
    return response.data
  }

  const uploadLanguagesTemplate = async (id: string, file: Blob, publish: boolean = false): Promise<boolean> => {
    const [response] = await executeRequest([
      SpsService.postLanguagesTemplate(id, file, publish),
    ])

    if (response.status >= 200 && response.status < 300) {
      return true
    }

    throw new LanguageTemplateValidationError('Language template upload invalid', response.data)
  }

  const fetchIndustries = async (): Promise<Industry[] | undefined> => {
    try {
      const [response] = await executeRequest([
        SpsService.getIndustries(),
      ])
      return response.data
    }
    catch {
      return undefined
    }
  }

  const updatePagination = (page: number, limit: number): void => {
    pagination.value.page = page
    pagination.value.limit = limit
  }

  const resetPagination = (): void => {
    pagination.value.page = 1
  }

  const fetchAllDemographicTypes = async (): Promise<DemographicType[]> => {
    try {
      const [response] = await executeRequest([
        SpsService.getDemographicTypes(),
      ])
      return response.data
    }
    catch {
      return undefined
    }
  }

  const fetchDemographicTypes = async (id: string): Promise<DemographicType[]> => {
    try {
      const [response] = await executeRequest([
        SpsService.getDemographicTypesByProject(id),
      ])

      return response.data
    }
    catch {
      return undefined
    }
  }

  const enqueueProgressReportPdf = async (id: string, demographics: string[]): Promise<ProgressReportPdfJob> => {
    const [response] = await executeRequest([
      SpsService.enqueueProgressReportPdf(id, demographics),
    ])
    return response.data
  }

  const downloadProgressReportPdfJob = async (id: string, jobId: string): Promise<Blob | undefined> => {
    const [response] = await executeRequest([
      SpsService.downloadProgressReportPdfJob(id, jobId),
    ])
    return response.data
  }

  const fetchCulturalStrengthFilters = async (id: string, lang: string): Promise<any> => {
    try {
      const [response] = await executeRequest([
        SpsService.getCulturalStrengthFilters(id, lang),
      ])
      if (response.status !== 200)
        throw Error

      return response.data
    }
    catch (error) {
      console.error(error)
      return undefined
    }
  }

  const fetchContrastDemographics = async (id: string, lang: string): Promise<ContrastDemographicOption[]> => {
    const [response] = await executeRequest([
      SpsService.getContrastDemographics(id, lang),
    ])
    if (response.status !== 200)
      throw new Error('Failed to load contrast demographics')
    return response.data
  }

  const fetchContrast = async (
    id: string,
    options: {
      demographic: string
      lang: string
      filters?: Record<string, string[]>
    },
  ): Promise<ContrastData | null> => {
    const [response] = await executeRequest([
      SpsService.getContrast(id, options),
    ])
    if (response.status !== 200)
      throw new Error('Failed to load contrast data')
    return response.data ?? null
  }

  const fetchQuestionBreakdownQuestions = async (id: string, lang: string): Promise<QuestionOption[]> => {
    const [response] = await executeRequest([
      SpsService.getQuestionBreakdownQuestions(id, lang),
    ])
    if (response.status !== 200)
      throw new Error('Failed to load question breakdown questions')
    return response.data
  }

  const fetchQuestionBreakdown = async (
    id: string,
    options: {
      questionId: string
      lang: string
      groupBy: QuestionBreakdownGroupBy
      filters?: Record<string, string[]>
    },
  ): Promise<QuestionBreakdownData | null> => {
    const [response] = await executeRequest([
      SpsService.getQuestionBreakdown(id, options),
    ])
    if (response.status !== 200)
      throw new Error('Failed to load question breakdown')
    return response.data ?? null
  }

  const fetchQuestionBreakdownByDemographic = async (
    id: string,
    options: {
      demographic: string
      question: string
      language?: string
      filters?: Record<string, string[]>
    },
  ): Promise<QuestionBreakdownByDemographicData | null> => {
    const [response] = await executeRequest([
      SpsService.getQuestionBreakdownByDemographic(id, options),
    ])
    if (response.status !== 200)
      throw new Error('Failed to load question breakdown by demographic')
    return response.data ?? null
  }

  const fetchCommentAnalysisSummary = async (
    id: string,
    options: {
      commentType: CommentAnalysisCommentType
      language?: string
      filters?: Record<string, string[]>
    },
  ): Promise<CommentAnalysisSummary | null> => {
    const [response] = await executeRequest([
      SpsService.getCommentAnalysisSummary(id, options),
    ])
    if (response.status !== 200)
      throw new Error('Failed to load comment analysis summary')
    return response.data ?? null
  }

  const fetchCommentAnalysisBySentiment = async (
    id: string,
    options: {
      commentType: CommentAnalysisCommentType
      sentiment?: CommentSentiment
      language?: string
      filters?: Record<string, string[]>
      page?: number
    },
  ): Promise<PaginatedCommentAnalysisSentiment> => {
    const [response] = await executeRequest([
      SpsService.getCommentAnalysisBySentiment(id, options),
    ])
    if (response.status !== 200)
      throw new Error('Failed to load comment analysis by sentiment')
    return response.data
  }

  const fetchCommentAnalysisByDemographic = async (
    id: string,
    options: {
      commentType: CommentAnalysisCommentType
      demographic: string
      language?: string
      filters?: Record<string, string[]>
    },
  ): Promise<CommentAnalysisByDemographicRow[]> => {
    const [response] = await executeRequest([
      SpsService.getCommentAnalysisByDemographic(id, options),
    ])
    if (response.status !== 200)
      throw new Error('Failed to load comment analysis by demographic')
    return response.data ?? []
  }

  const fetchCommentAnalysisByTheme = async (
    id: string,
    options: {
      commentType: CommentAnalysisCommentType
      sentiment?: CommentSentiment
      language?: string
      filters?: Record<string, string[]>
      page?: number
    },
  ): Promise<CommentAnalysisByTheme> => {
    const [response] = await executeRequest([
      SpsService.getCommentAnalysisByTheme(id, options),
    ])

    if (response.status !== 200)
      throw new Error('Failed to load comment analysis by sentiment')
    return response.data
  }

  const fetchCommentAnalysisByThemePage = async (
    projectId: string,
    themeId: string,
    options: {
      commentType: CommentAnalysisCommentType
      sentiment?: CommentSentiment
      language?: string
      filters?: Record<string, string[]>
      page?: number
    },
  ): Promise<CommentThemePage> => {
    const [response] = await executeRequest([
      SpsService.getCommentAnalysisThemePage(projectId, themeId, options),
    ])

    if (response.status !== 200)
      throw new Error('Failed to load comment analysis by sentiment')
    return response.data
  }

  const fetchContrastOverallSpreadByDemographic = async (
    id: string,
    options: {
      demographic: string
      language?: string
      filters?: Record<string, string[]>
    },
  ): Promise<ContrastByDemographicData | null> => {
    const [response] = await executeRequest([
      SpsService.getContrastOverallSpreadByDemographic(id, options),
    ])
    if (response.status !== 200)
      throw new Error('Failed to load contrast overall spread by demographic')
    return response.data ?? null
  }

  const exportProjectPpt = async (id: string, globalFilters: GlobalFilter[], lang: string): Promise<Blob | undefined> => {
    try {
      const [response] = await executeRequest([
        SpsService.exportProjectPpt(id, globalFilters, lang),
      ])
      return response.data
    }
    catch {
      return undefined
    }
  }

  const enqueueProjectPptMultiple = async (id: string, globalFilters: GlobalFilter[], splitCriteria: string, lang: string): Promise<ExportJob | undefined> => {
    try {
      const [response] = await executeRequest([
        SpsService.enqueueProjectPptMultiple(id, globalFilters, splitCriteria, lang),
      ])
      return response.data
    }
    catch {
      return undefined
    }
  }

  const enqueueProjectPptSingle = async (id: string, globalFilters: GlobalFilter[], lang: string): Promise<ExportJob | undefined> => {
    try {
      const [response] = await executeRequest([
        SpsService.enqueueProjectPptSingle(id, globalFilters, lang),
      ])
      return response.data
    }
    catch {
      return undefined
    }
  }

  const activateProject = async (id: string, content?: SpsSurvey | null): Promise<Project | undefined> => {
    try {
      const [response] = await executeRequest([
        SpsService.activateProject(id, content ? buildSurveyPayload(content) : undefined),
      ])
      return response.data
    }
    catch {
      return undefined
    }
  }

  return {
    projects: computed(() => surveys.value),
    pagination: computed(() => pagination.value),
    isLoading,
    hasError,
    totalSurveys,
    fetchProjects,
    fetchProjectDetails,
    createProject,
    updateProject,
    updatePagination,
    resetPagination,
    fetchIndustries,
    fetchSurveyJSON,
    updateSurveyJSON,
    downloadLanguagesTemplate,
    enqueueLanguagesTemplate,
    pollLanguagesTemplateJob,
    downloadLanguagesTemplateJob,
    cancelLanguagesTemplateJob,
    uploadLanguagesTemplate,
    fetchAllDemographicTypes,
    fetchDemographicTypes,
    enqueueProgressReportPdf,
    pollProgressReportPdfJob,
    downloadProgressReportPdfJob,
    cancelProgressReportPdfJob,
    exportProjectPpt,
    enqueueProjectPptMultiple,
    enqueueProjectPptSingle,
    fetchCulturalStrengthFilters,
    fetchContrastDemographics,
    fetchContrast,
    fetchQuestionBreakdownQuestions,
    fetchQuestionBreakdown,
    fetchQuestionBreakdownByDemographic,
    fetchCommentAnalysisSummary,
    fetchCommentAnalysisBySentiment,
    fetchCommentAnalysisByDemographic,
    fetchCommentAnalysisByTheme,
    fetchCommentAnalysisByThemePage,
    fetchContrastOverallSpreadByDemographic,
    activateProject,
  }
}
